import type {
  ClipboardEvent,
  ComponentPropsWithRef,
  KeyboardEvent,
} from 'react';
import { useCallback } from 'react';

type InputHandlerOptions = Pick<
  ComponentPropsWithRef<'input'>,
  'onKeyDown' | 'onPaste' | 'type'
>;

interface InputHandlers {
  onKeyDown: NonNullable<InputHandlerOptions['onKeyDown']>;
  onPaste: NonNullable<InputHandlerOptions['onPaste']>;
}

export function useInputHandlers({
  type,
  onKeyDown,
  onPaste,
}: InputHandlerOptions): InputHandlers {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (type === 'email' && event.key === ' ') {
        event.preventDefault();
      }

      onKeyDown?.(event);
    },
    [type, onKeyDown],
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      if (type === 'email') {
        const pastedText = event.clipboardData.getData('text');
        const trimmedText = pastedText.trim();

        if (pastedText !== trimmedText) {
          event.preventDefault();
          // execCommand preserves cursor position, undo, and React Hook Form events.
          document.execCommand('insertText', false, trimmedText);
        }
      }

      onPaste?.(event);
    },
    [type, onPaste],
  );

  return { onKeyDown: handleKeyDown, onPaste: handlePaste };
}
